import { TOOL_TYPE } from 'core/chatbot/utils/parsers/ToolType';
import { getScrollBehavior } from 'core/utils/motion';
import PropTypes from 'prop-types';
import { CONVERSATION_MODES } from '../../../constants/modes';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatMessage from '../ChatMessage/ChatMessage';
import TypingIndicator from '../TypingIndicator/TypingIndicator';
import './ChatMessages.scss';

// Shared stable reference for "no active tools", so a row without tools always receives the SAME
// array prop. A fresh `[]` per render would defeat the per-row memo (and React.memo(ChatMessage)).
const EMPTY_ACTIVE_TOOLS = [];

// Converting to a memo component to prevent unnecessary re-renders
const ChatMessages = React.memo(function ChatMessages({
  messages,
  streamingMessage,
  activeToolEvents,
  showDebugTools,
  mode,
  isLoading,
  readOnly,
  highlightedMessageId,
}) {
  const { t } = useTranslation(['chatbot']);
  const messagesEndRef = useRef(null);
  const [expandedContextId, setExpandedContextId] = useState(null);
  const containerRef = useRef(null);
  const isStreamingRef = useRef(false);

  const scrollToBottom = useCallback((isStreaming = false) => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // Use scrollHeight to account for ALL content including tool indicators
    if (isStreaming) {
      container.scrollTop = container.scrollHeight;
    } else {
      // Smooth scroll for normal messages
      container.scrollTo({
        top: container.scrollHeight,
        behavior: getScrollBehavior(),
      });
    }
  }, []);

  // Efecto para manejar el scroll durante el streaming
  useEffect(() => {
    if (streamingMessage?.isStreaming) {
      isStreamingRef.current = true;
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });
    } else {
      isStreamingRef.current = false;
    }
  }, [streamingMessage?.text, activeToolEvents, scrollToBottom]);

  // Efecto para manejar el scroll de mensajes normales
  useLayoutEffect(() => {
    if (!isStreamingRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });
    }
  }, [messages, activeToolEvents, scrollToBottom]);

  // Handle context expansion toggle
  const handleToggleContext = (messageId) => {
    setExpandedContextId((current) => (current === messageId ? null : messageId));
  };

  // Welcome message shown first in study mode. Memoized so its `new Date()` timestamp does not
  // rebuild a fresh object every render (ChatMessage never renders the timestamp, so freezing it is
  // invisible) — otherwise the welcome row would re-render on every streaming frame.
  const welcomeMessage = useMemo(
    () => ({
      id: 'welcome-message',
      sender: 'bot',
      text: t('Welcome message'),
      timestamp: new Date(),
      isError: false,
      isStreaming: false,
      justPosted: false,
      context: null,
    }),
    [t]
  );

  // Filtered/deduped tool events for the currently active bubble. Normalized through
  // EMPTY_ACTIVE_TOOLS so "no tools" is always the same reference (the raw expression could evaluate
  // to a boolean, which would still churn the row props).
  const activeTools = useMemo(() => {
    if (!activeToolEvents || activeToolEvents.length === 0) return EMPTY_ACTIVE_TOOLS;
    return activeToolEvents
      .filter(
        (event) =>
          event.toolType === TOOL_TYPE.MERMAID_DIAGRAM ||
          event.toolType === TOOL_TYPE.D3_VISUALIZATION ||
          event.toolType === TOOL_TYPE.GEOGEBRA_VISUALIZATION ||
          event.toolType === TOOL_TYPE.THREEJS_VISUALIZATION ||
          event.toolType === TOOL_TYPE.CANVAS_GENERATION ||
          event.toolType === TOOL_TYPE.PRACTICE_PROPOSAL
      )
      .filter((event, index, self) => index === self.findIndex((e) => e.toolName === event.toolName));
  }, [activeToolEvents]);

  // Historical (non-streaming) messages: welcome (study mode only) + real messages, minus empty user
  // messages. Kept separate from the streaming bubble so a streaming text update never rebuilds it.
  const historicalMessages = useMemo(
    () => [
      ...(!readOnly && mode === CONVERSATION_MODES.STUDY ? [welcomeMessage] : []),
      ...messages.filter((msg) => msg.sender === 'bot' || (msg.text && msg.text.trim()) || msg.image || (msg.attachments && msg.attachments.length > 0)),
    ],
    [messages, mode, readOnly, welcomeMessage]
  );

  const hasStreamingMessage = !!streamingMessage;

  // `activeTools` land on the last historical row ONLY when nothing is streaming; otherwise they
  // belong to the streaming bubble. Resolved OUTSIDE the memo below so that during streaming this
  // stays pinned to the EMPTY_ACTIVE_TOOLS reference — per-frame tool events then cannot churn the
  // historical rows' deps.
  const lastHistoricalRowActiveTools = hasStreamingMessage ? EMPTY_ACTIVE_TOOLS : activeTools;

  // Memoized historical rows. A streaming-only update changes `streamingMessage` (an isolated
  // sibling) but leaves every dep here unchanged, so the SAME element array is returned and React
  // skips re-rendering every historical ChatMessage row.
  const historicalRows = useMemo(
    () =>
      historicalMessages.map((message, index) => (
        <ChatMessage
          key={message.id}
          highlighted={message.highlighted || (highlightedMessageId ? message.id === highlightedMessageId : false)}
          message={{
            id: message.id,
            sender: message.sender,
            text: message.text,
            timestamp: message.timestamp,
            isError: message.isError,
            isFlagged: message.isFlagged,
            isStreaming: message.isStreaming,
            justPosted: message.justPosted,
            context: showDebugTools ? message.context : null,
            image: message.image,
            imageName: message.imageName,
            attachments: message.attachments,
            flagCategories: message.flagCategories,
          }}
          activeTools={index === historicalMessages.length - 1 ? lastHistoricalRowActiveTools : EMPTY_ACTIVE_TOOLS}
          className=""
          readOnly={readOnly}
        />
      )),
    [historicalMessages, lastHistoricalRowActiveTools, showDebugTools, highlightedMessageId, readOnly]
  );

  // Show loading indicator while conversation is loading
  if (isLoading) {
    return (
      <div className="chat-messages chat-messages--loading" ref={containerRef}>
        <div className="chat-messages__container">
          <div className="chat-messages__loading">
            <TypingIndicator />
          </div>
        </div>
      </div>
    );
  }

  // Streaming bubble decoration (applies ONLY to the streaming message). expandedContext/
  // onToggleContext are preserved verbatim from the previous single-list build.
  const completeStreamingMessage = streamingMessage
    ? {
        ...streamingMessage,
        expandedContext: expandedContextId === streamingMessage.id,
        onToggleContext: () => handleToggleContext(streamingMessage.id),
      }
    : null;

  return (
    <div className="chat-messages" ref={containerRef}>
      <div className="chat-messages__container">
        {historicalRows}

        {/* Streaming bubble isolated as a sibling so its per-frame updates never re-render the
            memoized historical rows above. DOM order (last child before the scroll anchor) matches
            the previous single-list layout. */}
        {completeStreamingMessage && (
          <ChatMessage
            key={completeStreamingMessage.id}
            highlighted={
              completeStreamingMessage.highlighted || (highlightedMessageId ? completeStreamingMessage.id === highlightedMessageId : false)
            }
            message={{
              id: completeStreamingMessage.id,
              sender: completeStreamingMessage.sender,
              text: completeStreamingMessage.text,
              timestamp: completeStreamingMessage.timestamp,
              isError: completeStreamingMessage.isError,
              isFlagged: completeStreamingMessage.isFlagged,
              isStreaming: completeStreamingMessage.isStreaming,
              justPosted: completeStreamingMessage.justPosted,
              context: showDebugTools ? completeStreamingMessage.context : null,
              image: completeStreamingMessage.image,
              imageName: completeStreamingMessage.imageName,
              attachments: completeStreamingMessage.attachments,
              flagCategories: completeStreamingMessage.flagCategories,
            }}
            activeTools={activeTools}
            className=""
            readOnly={readOnly}
          />
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
});

ChatMessages.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      sender: PropTypes.oneOf(['user', 'bot']).isRequired,
      text: PropTypes.string.isRequired,
      timestamp: PropTypes.instanceOf(Date),
      isError: PropTypes.bool,
      context: PropTypes.object,
      image: PropTypes.string,
      imageName: PropTypes.string,
      attachments: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string.isRequired,
          contentType: PropTypes.string.isRequired,
          fileName: PropTypes.string.isRequired,
          sizeInBytes: PropTypes.number.isRequired,
          createdAt: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  streamingMessage: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sender: PropTypes.oneOf(['user', 'bot']).isRequired,
    text: PropTypes.string.isRequired,
    timestamp: PropTypes.instanceOf(Date),
    isStreaming: PropTypes.bool,
    isError: PropTypes.bool,
    context: PropTypes.object,
    image: PropTypes.string,
    imageName: PropTypes.string,
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        contentType: PropTypes.string.isRequired,
        fileName: PropTypes.string.isRequired,
        sizeInBytes: PropTypes.number.isRequired,
        createdAt: PropTypes.string.isRequired,
      })
    ),
  }),
  activeToolEvents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.oneOf(['tool_invocation', 'tool_completion']),
      toolName: PropTypes.string,
      toolType: PropTypes.oneOf([
        'd3_visualization',
        'mermaid_diagram',
        'content_retrieval',
        'geogebra_visualization',
        'threejs_visualization',
        'web_search',
        'canvas_generation',
        'unknown',
      ]),
      status: PropTypes.oneOf(['started', 'completed', 'failed']),
      timestamp: PropTypes.string,
      args: PropTypes.any,
      result: PropTypes.any,
    })
  ),
  showDebugTools: PropTypes.bool,
  mode: PropTypes.oneOf(Object.values(CONVERSATION_MODES)),
  isLoading: PropTypes.bool,
  readOnly: PropTypes.bool,
  highlightedMessageId: PropTypes.string,
};

ChatMessages.defaultProps = {
  messages: [],
  streamingMessage: null,
  activeToolEvents: [],
  showDebugTools: false,
  mode: CONVERSATION_MODES.STUDY,
  isLoading: false,
  readOnly: false,
  highlightedMessageId: null,
};

export default ChatMessages;
